import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DataGrid,
  Input,
  Spinner,
  useDataGridColumnsState,
} from '@ibsys/kit';
import type { DataGridLeafColumn } from '@ibsys/kit';
import { QualityDetailItem, type HeatmapMode } from '@/entities/dashboards';
import { StatBox } from './parts/StatBox';
import { fmt, getMonthKey, getWeekKey, safeAvg, scoreClass, totalScoreClass } from './utils';

type Props = {
  items: QualityDetailItem[];
  blockNames: string[];
  isLoading: boolean;
  drill: { consultant: string; period: string } | null;
  selectedCallId: number | null;
  heatmapMode: HeatmapMode;
  scoreFrom: string;
  scoreTo: string;
  onChangeScoreFrom: (v: string) => void;
  onChangeScoreTo: (v: string) => void;
  onResetScore: () => void;
  onSelectCall: (id: number) => void;
};

/**
 * Карточка drill-down: список звонков выбранной ячейки heatmap
 * (консультант × период) с дополнительной фильтрацией по диапазону баллов.
 */
export const PeriodDetailsCard: FC<Props> = ({
  items,
  blockNames,
  isLoading,
  drill,
  selectedCallId,
  heatmapMode,
  scoreFrom,
  scoreTo,
  onChangeScoreFrom,
  onChangeScoreTo,
  onResetScore,
  onSelectCall,
}) => {
  const { t } = useTranslation('qualityDashboard');

  const rows = useMemo(() => {
    if (!drill) return [];
    const periodKey = (date: string | null) =>
      heatmapMode === 'month' ? getMonthKey(date) : getWeekKey(date);

    let filtered = items.filter(
      (r) => r.consultantName === drill.consultant && periodKey(r.evaluationDate) === drill.period,
    );
    const from = scoreFrom ? parseFloat(scoreFrom) : null;
    const to = scoreTo ? parseFloat(scoreTo) : null;
    if (from !== null) {
      filtered = filtered.filter((r) => r.totalRating !== null && r.totalRating >= from);
    }
    if (to !== null) {
      filtered = filtered.filter((r) => r.totalRating !== null && r.totalRating <= to);
    }
    return filtered.sort((a, b) => (b.evaluationDate ?? '').localeCompare(a.evaluationDate ?? ''));
  }, [items, drill, heatmapMode, scoreFrom, scoreTo]);

  const stats = useMemo(() => {
    const ratings = rows.map((r) => r.totalRating).filter((v): v is number => v !== null);
    if (ratings.length === 0) return null;
    return {
      count: rows.length,
      avg: safeAvg(ratings),
      min: Math.min(...ratings),
      max: Math.max(...ratings),
    };
  }, [rows]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('periodDetails.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        <ScoreRangeFilter
          scoreFrom={scoreFrom}
          scoreTo={scoreTo}
          onChangeScoreFrom={onChangeScoreFrom}
          onChangeScoreTo={onChangeScoreTo}
          onResetScore={onResetScore}
        />

        {!drill ? (
          <div className="text-muted-foreground text-sm">{t('periodDetails.hint')}</div>
        ) : isLoading ? (
          <div className="flex justify-center py-8">
            <Spinner className="size-6" />
          </div>
        ) : rows.length === 0 ? (
          <div className="text-muted-foreground text-sm">{t('periodDetails.noDataInCell')}</div>
        ) : (
          <>
            {stats && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                <StatBox label={t('periodDetails.stats.calls')} value={stats.count} />
                <StatBox label={t('periodDetails.stats.avgScore')} value={fmt(stats.avg)} />
                <StatBox
                  label={t('periodDetails.stats.minMax')}
                  value={`${fmt(stats.min)} / ${fmt(stats.max)}`}
                />
              </div>
            )}
            {/* При смене набора блоков пересоздаём таблицу для сброса состояния колонок */}
            <DrillTable
              key={blockNames.join(',')}
              rows={rows}
              blockNames={blockNames}
              selectedCallId={selectedCallId}
              onSelectCall={onSelectCall}
            />
          </>
        )}
      </CardContent>
    </Card>
  );
};

// ============ Вспомогательные секции ============

type ScoreRangeFilterProps = {
  scoreFrom: string;
  scoreTo: string;
  onChangeScoreFrom: (v: string) => void;
  onChangeScoreTo: (v: string) => void;
  onResetScore: () => void;
};

const ScoreRangeFilter: FC<ScoreRangeFilterProps> = ({
  scoreFrom,
  scoreTo,
  onChangeScoreFrom,
  onChangeScoreTo,
  onResetScore,
}) => {
  const { t } = useTranslation('qualityDashboard');
  return (
    <div className="flex gap-3 items-end flex-wrap mb-4">
      <Input
        type="number"
        label={t('periodDetails.scoreFrom')}
        value={scoreFrom}
        onChange={(e) => onChangeScoreFrom(e.target.value)}
        placeholder={t('periodDetails.scoreFromPlaceholder')}
        className="w-32"
      />
      <Input
        type="number"
        label={t('periodDetails.scoreTo')}
        value={scoreTo}
        onChange={(e) => onChangeScoreTo(e.target.value)}
        placeholder={t('periodDetails.scoreToPlaceholder')}
        className="w-32"
      />
      <Button variant="secondary" size="sm" onClick={onResetScore}>
        {t('periodDetails.reset')}
      </Button>
    </div>
  );
};

type DrillTableProps = {
  rows: QualityDetailItem[];
  blockNames: string[];
  selectedCallId: number | null;
  onSelectCall: (id: number) => void;
};

const DrillTable: FC<DrillTableProps> = ({ rows, blockNames, selectedCallId, onSelectCall }) => {
  const { t } = useTranslation('qualityDashboard');

  const columns = useMemo((): DataGridLeafColumn<QualityDetailItem>[] => {
    const fixed: DataGridLeafColumn<QualityDetailItem>[] = [
      {
        id: 'date',
        header: t('periodDetails.columns.date'),
        accessor: 'evaluationDate',
        size: 120,
        cell: ({ value }) => (value as string | null) ?? '—',
      },
      {
        id: 'client',
        header: t('periodDetails.columns.client'),
        accessor: 'clientName',
        size: 180,
        cell: ({ value }) => (value as string) || '—',
      },
      {
        id: 'externalId',
        header: t('periodDetails.columns.externalId'),
        accessor: 'externalId',
        size: 120,
        cell: ({ value }) => (value as string) || '—',
      },
      {
        id: 'totalScore',
        header: t('periodDetails.columns.totalScore'),
        accessor: 'totalRating',
        size: 110,
        align: 'center',
        className: 'font-bold',
        cellClassName: ({ value }) => totalScoreClass(value as number | null),
        cell: ({ value }) => fmt(value as number | null),
      },
    ];

    return [
      ...fixed,
      ...blockNames.map(
        (n): DataGridLeafColumn<QualityDetailItem> => ({
          id: `block_${n}`,
          header: n,
          accessor: (row) => row.blocks.find((b) => b.blockName === n)?.rating ?? null,
          size: 110,
          align: 'center',
          className: 'font-bold',
          cellClassName: ({ value }) => scoreClass(value as number | null),
          cell: ({ value }) => fmt(value as number | null),
        }),
      ),
    ];
  }, [t, blockNames]);

  const { gridProps, panelProps } = useDataGridColumnsState({
    defaultOrder: columns.map((c) => c.id),
    defaultPinning: { left: ['date', 'client'], right: [] },
  });

  return (
    <DataGrid
      {...gridProps}
      columns={columns}
      dataSource={{ mode: 'client', data: rows }}
      getRowId={(r) => String(r.id)}
      enableColumnResizing
      enableColumnReordering
      enableColumnPinning
      columnsPanel={{ showPinning: true, onReset: panelProps.onReset }}
      // activeRowId подсвечивает строку выбранного звонка (открыта детальная карточка).
      activeRowId={selectedCallId !== null ? String(selectedCallId) : null}
      onRowClick={(row) => onSelectCall(row.id)}
      height={384}
    />
  );
};
